// #1384 旅先で役に立つお知らせ枠。
// 公開取得は**認証不要** — ゲスト閲覧中の旅行者にも出す（ログインを求めた時点で、
// その場で役に立つ案内という価値が消える）。配置ルールは index.ts 参照。
import { API_BASE, apiFetch, trackedFetch } from './_client'

export type AnnouncementCategory =
  | 'transport' | 'charging' | 'food' | 'experience' | 'other'

/** 顧客に出すお知らせ（運営用の weight などは含まれない）。 */
export type Announcement = {
  id: string
  title: string
  body: string
  imageUrl: string
  linkUrl: string
  category: AnnouncementCategory
  // 掲載料を受け取っている枠。true なら **PR ラベルを必ず表示する**
  // （景品表示法のステマ規制。表示を省くと違反になる）。
  sponsored: boolean
}

export type AnnouncementAreaMode = 'anywhere' | 'radius' | 'residence'

/** 運営が編集する全項目。 */
export type AnnouncementAdmin = Announcement & {
  titleEn: string
  bodyEn: string
  // 対象の決め方 (定義はサーバの services/announcements.py)。
  // residence = その町に住んでいる人 (#2004。居住地を答えていない人・ゲストには出ない)
  areaMode: AnnouncementAreaMode
  lat: number | null
  lng: number | null
  radiusKm: number | null
  // areaMode='residence' の対象の町。2 つ一組で、サーバのマスタにあるものだけ。他のモードでは ''
  targetPrefecture: string
  targetCity: string
  startAt: string
  endAt: string
  active: boolean
  weight: number
  // #1384 Phase 2 push で送ってよいか。**既定オフ** — ここを緩めると、いずれ
  // 全部が push になって通知ごと切られる。イベント系だけ運営が明示的にオンにする。
  pushEnabled: boolean
  // #2004 メールでも送る (町あてのときだけ)。既定オフ
  mailEnabled: boolean
  // #2004 ⑤ メール用の件名・本文 (空ならお知らせの題名・本文)。町あてのときだけ保存される
  mailSubject: string
  mailBody: string
  // 添付 (保存済みのお知らせにだけ付けられる。別の口で預かるので入力には含めない)
  mailAttachments?: AnnouncementAttachment[]
  impressions?: number
  clicks?: number
}

export type AnnouncementAttachment = {
  id: string
  name: string
  size: number
  contentType: string
}

export type AnnouncementInput = Omit<AnnouncementAdmin, 'id' | 'impressions' | 'clicks' | 'mailAttachments'>

export const announcementsApi = {
  /** 現在地に合うお知らせ。認証不要なので素の fetch を使う。 */
  async fetchAnnouncements(params: {
    lat?: number | null
    lng?: number | null
    locale: string
    excludeIds?: string[]
    limit?: number
    /** #2004 ログイン中の人の居住地 (県と市が揃っているときだけ)。住んでいる町あての案内を
     * 受け取るために端末が申告する。認証不要の口なので、サーバはこれ以外に知る手段が無い */
    residence?: { prefecture: string; city: string } | null
  }) {
    const q = new URLSearchParams({ locale: params.locale, limit: String(params.limit ?? 1) })
    if (params.residence?.prefecture && params.residence.city) {
      q.set('residencePrefecture', params.residence.prefecture)
      q.set('residenceCity', params.residence.city)
    }
    if (params.lat != null && params.lng != null) {
      q.set('lat', String(params.lat))
      q.set('lng', String(params.lng))
    }
    if (params.excludeIds?.length) q.set('exclude', params.excludeIds.join(','))
    const res = await trackedFetch(`${API_BASE}/announcements/active?${q.toString()}`)
    if (!res.ok) throw new Error(`announcements ${res.status}`)
    return (await res.json()).announcements as Announcement[]
  },

  /** 計測。**失敗しても画面を止めない**ので呼び出し側で握り潰す前提。 */
  async recordAnnouncementImpression(id: string) {
    await trackedFetch(`${API_BASE}/announcements/${id}/impression`, { method: 'POST' })
  },

  async recordAnnouncementClick(id: string) {
    await trackedFetch(`${API_BASE}/announcements/${id}/click`, { method: 'POST' })
  },

  // ---- 運営 (ADMIN_EMAILS ゲート) ----
  async listAllAnnouncements() {
    const res = await apiFetch('/announcements/admin/list')
    return (await res.json()).announcements as AnnouncementAdmin[]
  },

  async createAnnouncement(body: AnnouncementInput) {
    const res = await apiFetch('/announcements/admin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    return res.json() as Promise<AnnouncementAdmin>
  },

  async updateAnnouncement(id: string, body: AnnouncementInput) {
    const res = await apiFetch(`/announcements/admin/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    return res.json() as Promise<AnnouncementAdmin>
  },

  /** #1384 Phase 2 このお知らせを push 配信する。**運営がボタンで実行する** —
   * 深夜配信や誤爆を機械に任せると取り返しがつかないため。
   * skipped は除外理由の内訳（0件のとき設定ミスか対象不在かを判別するため）。
   * #2004 `confirm` は previewAnnouncementPush が返した合言葉 (必須)。確認の後にお知らせや
   * 宛先が変わっていたら、サーバは送らずに 409 で断る (apiFetch が ApiError を投げる)。 */
  async sendAnnouncementPush(id: string, confirm: string) {
    const res = await apiFetch(`/announcements/admin/${id}/push`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirm }),
    })
    return res.json() as Promise<{
      sent: number; targeted: number; skipped: Record<string, number>
    }>
  },

  /** #2004 メールで送る前に「何人に届くか」を見る。何も送らない。
   * configured が false なら、送る鍵が無い環境 (ローカル) で、送っても届かない。 */
  async previewAnnouncementMail(id: string) {
    const res = await apiFetch(`/announcements/admin/${id}/mail-preview`)
    return res.json() as Promise<{
      people: number; skipped: Record<string, number>; configured: boolean; confirm: string
    }>
  },

  /** #2004 このお知らせを、対象の町に住んでいる人へメールで送る。confirm は mail-preview の合言葉。 */
  async sendAnnouncementMail(id: string, confirm: string) {
    const res = await apiFetch(`/announcements/admin/${id}/mail`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirm }),
    })
    // failed = 送ろうとして送れなかった人 (印が無いので、もう一度送れば届く)
    return res.json() as Promise<{
      sent: number; targeted: number; failed: number; skipped: Record<string, number>
    }>
  },

  /** #2004 ⑤ 自分 (ログイン中の管理者) に 1 通送って実物を見る。誰にも印は付かない。 */
  async sendAnnouncementMailTest(id: string) {
    const res = await apiFetch(`/announcements/admin/${id}/mail-test`, { method: 'POST' })
    return res.json() as Promise<{ sent: boolean; reason: string | null }>
  },

  /** #2004 ⑤ メールの添付を 1 つ預ける (PDF・PNG・JPEG、3 つまで・合計 5 MB)。 */
  async addAnnouncementAttachment(id: string, file: File) {
    const fd = new FormData()
    fd.append('file', file, file.name)
    const res = await apiFetch(`/announcements/admin/${id}/attachments`, { method: 'POST', body: fd })
    return res.json() as Promise<{ attachments: AnnouncementAttachment[] }>
  },

  async removeAnnouncementAttachment(id: string, attachmentId: string) {
    const res = await apiFetch(`/announcements/admin/${id}/attachments/${attachmentId}`, { method: 'DELETE' })
    return res.json() as Promise<{ attachments: AnnouncementAttachment[] }>
  },

  /** #2004 メールの停止リンクから。認証不要 (合言葉で本人性を担保)。 */
  async unsubscribeResidenceMail(u: string, t: string) {
    const res = await trackedFetch(`${API_BASE}/announcements/mail/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ u, t }),
    })
    if (!res.ok) throw new Error(`unsubscribe ${res.status}`)
    return (await res.json()) as { ok: boolean }
  },

  /** #2004 送る前に「何人に届くか」を見る。何も送らない。people は人、devices は端末
   * (どちらも上限。端末の側で購読が切れていれば、実際に届く数は少なくなる)。
   * confirm は、この確認の内容の合言葉 — 送るときにそのまま渡す。 */
  async previewAnnouncementPush(id: string) {
    const res = await apiFetch(`/announcements/admin/${id}/push-preview`)
    return res.json() as Promise<{
      devices: number; people: number; skipped: Record<string, number>; confirm: string
    }>
  },

  async deleteAnnouncement(id: string) {
    const res = await apiFetch(`/announcements/admin/${id}`, { method: 'DELETE' })
    return res.json() as Promise<{ deleted: boolean }>
  },
}
